/* eslint-disable react/prop-types */ // TODO: add props
import { useEffect, useCallback, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import PT from 'prop-types';
import { useActivity, useActivityResponse } from './hooks';
import { useClassmates } from '../bySchoolClass';
import { Container, TabNav, SaveStatus } from 'edrolo-style-guide';
import { StudentResponses, ActivityTab } from './components';
import { Activity, ErrorAlert } from 'core/components';
import styles from './styles.scss';
import LoadingIndicator from '../app/components/LoadingIndicator';
import { useInjectable } from 'core/hooks';
import { useAnalytics } from 'edrolo/analytics';

export const ActivityContainer = ({
  activityId,
  nextUrl,
  isTeacherView,
  classId,
  chunkId,
  lessonId,
  recordChunkVisit,
}) => {
  const { track } = useAnalytics({
    eventAttributes: {
      activity_id: activityId,
    },
    throttle: 60000,
  });

  const useActivityInjectable = useInjectable(
    'useActivityInjectable',
    useActivity
  );
  const useActivityResponseInjectable = useInjectable(
    'useActivityResponseInjectable',
    useActivityResponse
  );
  const useClassmatesInjectable = useInjectable(
    'useClassmatesInjectable',
    useClassmates
  );

  const {
    data: activity,
    error: activityError,
    loading: activityIsLoading,
  } = useActivityInjectable(activityId);

  const {
    activityResponse,
    status,
    activityResponseIsLoading,
    activityResponseError,
    saveActivityResponse,
    responseSaveError,
  } = useActivityResponseInjectable(activityId);

  const [tabId, setTabId] = useState('activity');
  const [error, setError] = useState();

  const { data: classmates } = useClassmatesInjectable(classId);

  const tabItems = [
    { id: 'activity', label: 'View activity' },
    {
      id: 'example',
      label: 'Example answer',
      disabled: !isTeacherView && !activityResponse?.ready_for_review,
    },
  ];

  if (isTeacherView) {
    tabItems.push({
      id: 'responses',
      label: 'View student responses',
    });
  }

  useEffect(() => {
    setTabId('activity');
  }, [chunkId]);

  // Record a chunk visit when the user first visits the activity.
  // This needs to be called explicitly because activities are rendered
  // outside of the ChunkCarousel component, which usually handles the
  // recording of chunk visits.
  useEffect(() => {
    recordChunkVisit(lessonId, chunkId);
  }, [lessonId, chunkId]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setError(activityError || activityResponseError || responseSaveError);
  }, [activityError, activityResponseError, responseSaveError]);

  // We need debounce to not make new instances on each render or it wont debounce.
  const debouncedSaveActivityResponse = useMemo(
    () =>
      debounce(saveActivityResponse, 1000, {
        leading: true,
        trailing: true,
      }),
    [saveActivityResponse]
  );

  const handleActivityResponseChange = useCallback(
    (updatedActivityResponse) => {
      debouncedSaveActivityResponse(updatedActivityResponse);
      track('activity response updated');
    },
    [debouncedSaveActivityResponse, track]
  );

  if (activityIsLoading || activityResponseIsLoading || !classmates) {
    return <LoadingIndicator loadingText="Loading activity" />;
  }

  if ((!activity || !activityResponse) && error) {
    return (
      <section className={styles.activityContainer}>
        <Container>
          <ErrorAlert
            statusCode={activityError?.status || activityResponseError?.status}
          />
        </Container>
      </section>
    );
  }

  const collabModalOptions = classmates?.map((student) => ({
    value: student.profile_id,
    label: student.display_name,
  }));

  return (
    <section className={styles.activityContainer}>
      <Container>
        <header className={styles.header}>
          <div className={styles.nav}>
            <TabNav
              items={tabItems}
              selectedItemId={tabId}
              onChange={(id) => setTabId(id)}
            />
            <div className={styles.saveStatus}>
              <SaveStatus status={status} />
            </div>
          </div>

          {error && (
            <div className={styles.errorAlert}>
              <ErrorAlert statusCode={error.status} />
            </div>
          )}
        </header>

        {tabId === 'example' && (
          <Activity activity={activity} showExample readOnly />
        )}

        {tabId === 'responses' && (
          <StudentResponses
            activity={activity}
            classId={classId}
            lessonId={lessonId}
            collabModalOptions={collabModalOptions}
            students={classmates}
          />
        )}

        {tabId === 'activity' && (
          <ActivityTab
            students={classmates}
            isTeacherView={isTeacherView}
            nextUrl={nextUrl}
            activity={activity}
            saveActivityResponse={saveActivityResponse}
            activityResponse={activityResponse}
            onActivityResponseChange={handleActivityResponseChange}
            collabModalOptions={collabModalOptions}
          />
        )}
      </Container>
    </section>
  );
};

ActivityContainer.propTypes = {
  activityId: PT.string,
  nextUrl: PT.string,
  isTeacherView: PT.bool,
  classId: PT.string,
};
